import React, { createContext, useContext, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchUnreadCount } from '@/data/advisor-chat';
import { fetchSupportUnread } from '@/data/support';
import { useAuth } from './AuthContext';
import { useOrgs } from './OrgsProvider';

export type SupportTarget = { orgId?: string; draft?: string; label?: string };

interface ChatContextType {
  isOpen: boolean;
  openChat: (conversationId?: string) => void;
  openSupport: (target?: SupportTarget) => void;
  support: SupportTarget | null;
  setSupport: (target: SupportTarget | null) => void;
  closeChat: () => void;
  activeConversationId: string | null;
  setActiveConversationId: (id: string | null) => void;
  unread: number;
  supportUnread: number;
  roles: Array<'customer' | 'advisor'>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

const UNREAD_POLL_MS = 20000;

export const ChatProvider = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const { activeOrg } = useOrgs();
  const [isOpen, setIsOpen] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [support, setSupport] = useState<SupportTarget | null>(null);

  const { data } = useQuery({
    queryKey: ['chat-unread', activeOrg?.id],
    queryFn: fetchUnreadCount,
    enabled: isAuthenticated && Boolean(activeOrg?.id),
    refetchInterval: UNREAD_POLL_MS,
    retry: false,
  });

  const supportUnread = useQuery({
    queryKey: ['support-unread', activeOrg?.id],
    queryFn: () => fetchSupportUnread(),
    enabled: isAuthenticated && Boolean(activeOrg?.id),
    refetchInterval: UNREAD_POLL_MS,
    retry: false,
  });

  const openChat = (conversationId?: string) => {
    setSupport(null);
    setActiveConversationId(conversationId ?? null);
    setIsOpen(true);
  };

  const openSupport = (target: SupportTarget = {}) => {
    setSupport(target);
    setIsOpen(true);
  };

  return (
    <ChatContext.Provider
      value={{
        isOpen,
        openChat,
        openSupport,
        support,
        setSupport,
        closeChat: () => setIsOpen(false),
        activeConversationId,
        setActiveConversationId,
        unread: (data?.unread ?? 0) + (supportUnread.data?.unread ?? 0),
        supportUnread: supportUnread.data?.unread ?? 0,
        roles: data?.roles ?? [],
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
